<template>
  <div class="logic-check-wrapper" v-loading.fullscreen.lock="loading">
    <!-- 游戏选择 -->
    <div class="game-selector">
      <el-select v-model="currentGameId" placeholder="选择游戏" @change="onGameSelect" filterable style="width: 300px;">
        <el-option v-for="g in allGames" :key="g.id" :label="g.title" :value="g.id" />
      </el-select>
    </div>

    <!-- 查询区域 -->
    <div class="filter-section">
      <el-card shadow="hover">
        <div class="filter-content">
          <div class="filter-item">
            <label class="filter-label">用户ID：</label>
            <el-input
              v-model="userId"
              placeholder="请输入用户ID"
              style="width: 200px;"
              @keyup.enter.native="handleSearch"
            />
          </div>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
        </div>
      </el-card>
    </div>

    <template v-if="resultData">
      <!-- 用户基本信息 -->
      <div class="result-section">
        <h3 class="section-title">用户基本信息</h3>
        <el-table :data="[resultData.user_info]" style="width: 100%" border>
          <el-table-column prop="user_id" label="用户ID" width="100" />
          <el-table-column prop="username" label="用户昵称" width="150" />
          <el-table-column label="注册时间" width="180">
            <template slot-scope="scope">{{ formatTime(scope.row.created_at) }}</template>
          </el-table-column>
          <el-table-column prop="record_id" label="记录ID" width="100" />
          <el-table-column label="最大章节">
            <template slot-scope="scope">
              {{ scope.row.max_chapter_title || '-' }}
              <span v-if="scope.row.max_chapter" style="color: #999;">(ID: {{ scope.row.max_chapter }})</span>
            </template>
          </el-table-column>
          <el-table-column prop="device_model" label="手机型号" width="150">
            <template slot-scope="scope">{{ scope.row.device_model || '-' }}</template>
          </el-table-column>
          <el-table-column prop="app_version" label="App版本" width="100">
            <template slot-scope="scope">{{ scope.row.app_version || '-' }}</template>
          </el-table-column>
          <el-table-column prop="user_message_count" label="用户发送消息数" width="130" />
        </el-table>
      </div>

      <!-- AI剧情奖励 -->
      <div class="result-section">
        <h3 class="section-title">数值奖励 - AI剧情奖励 (chapter_reward)</h3>

        <div class="sub-section">
          <h4 class="sub-title">属性奖励总计</h4>
          <el-table
            :data="resultData.attribute_rewards"
            style="width: 100%"
            border
            empty-text="暂无属性奖励数据"
          >
            <el-table-column prop="attribute_name" label="属性名称" />
            <el-table-column prop="total_change" label="总变化值">
              <template slot-scope="scope">
                <span :style="{ color: scope.row.total_change >= 0 ? '#67c23a' : '#f56c6c' }">
                  {{ scope.row.total_change >= 0 ? '+' : '' }}{{ scope.row.total_change }}
                </span>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="sub-section">
          <h4 class="sub-title">好感度总值</h4>
          <el-table
            :data="resultData.affection_rewards"
            style="width: 100%"
            border
            empty-text="暂无好感度数据"
          >
            <el-table-column prop="role_name" label="角色" />
            <el-table-column prop="affection_value" label="当前好感度" />
          </el-table>
        </div>

        <div class="sub-section">
          <h4 class="sub-title">成就总数</h4>
          <div class="stat-value">{{ resultData.achievement_count }}</div>
        </div>
      </div>

      <!-- 服装解锁情况 -->
      <div class="result-section">
        <h3 class="section-title">服装解锁情况</h3>
        <template v-if="resultData.costumes.length > 0">
          <el-card
            v-for="group in resultData.costumes"
            :key="group.role_name"
            class="costume-card"
            shadow="never"
          >
            <div slot="header" class="costume-card-header">
              <span>{{ group.role_name }}</span>
              <el-tag size="small">{{ group.costumes.length }}套</el-tag>
            </div>
            <el-table :data="group.costumes" style="width: 100%" border>
              <el-table-column prop="costume_name" label="服装名称" width="150" />
              <el-table-column prop="price" label="价格" width="80" />
              <el-table-column label="购买时间" width="180">
                <template slot-scope="scope">{{ formatTime(scope.row.purchase_time) }}</template>
              </el-table-column>
              <el-table-column label="购买时所在节点">
                <template slot-scope="scope">
                  <template v-if="scope.row.node_info">
                    <div>{{ scope.row.node_info.node_title }}</div>
                    <div v-if="scope.row.node_info.entry_conditions" class="entry-conditions">
                      <el-tag
                        v-for="(cond, idx) in formatConditions(scope.row.node_info.entry_conditions)"
                        :key="idx"
                        size="mini"
                        type="info"
                        style="margin: 2px;"
                      >{{ cond }}</el-tag>
                    </div>
                  </template>
                  <span v-else style="color: #999;">-</span>
                </template>
              </el-table-column>
            </el-table>
          </el-card>
        </template>
        <div v-else class="empty-text">暂无服装数据</div>
      </div>

      <!-- 物品购买情况 -->
      <div class="result-section">
        <h3 class="section-title">物品购买情况</h3>

        <div class="sub-section">
          <h4 class="sub-title">好感度道具</h4>
          <template v-if="resultData.affection_purchases.length > 0">
            <el-card
              v-for="group in resultData.affection_purchases"
              :key="group.role_name"
              class="purchase-card"
              shadow="never"
            >
              <div slot="header" class="costume-card-header">
                <span>{{ group.role_name }}</span>
                <span class="purchase-summary">
                  总次数: {{ group.total_quantity }} | 总值: +{{ group.total_value }}
                </span>
              </div>
              <el-table :data="group.purchases" style="width: 100%" border>
                <el-table-column prop="quantity" label="数量" width="80" />
                <el-table-column prop="value" label="好感度值" width="100">
                  <template slot-scope="scope">+{{ scope.row.value }}</template>
                </el-table-column>
                <el-table-column label="购买时间" width="180">
                  <template slot-scope="scope">{{ formatTime(scope.row.purchase_time) }}</template>
                </el-table-column>
                <el-table-column label="购买时所在节点">
                  <template slot-scope="scope">
                    <template v-if="scope.row.node_info">
                      <div>{{ scope.row.node_info.node_title }}</div>
                      <div v-if="scope.row.node_info.entry_conditions" class="entry-conditions">
                        <el-tag
                          v-for="(cond, idx) in formatConditions(scope.row.node_info.entry_conditions)"
                          :key="idx"
                          size="mini"
                          type="info"
                          style="margin: 2px;"
                        >{{ cond }}</el-tag>
                      </div>
                    </template>
                    <span v-else style="color: #999;">-</span>
                  </template>
                </el-table-column>
              </el-table>
            </el-card>
          </template>
          <div v-else class="empty-text">暂无好感度道具购买数据</div>
        </div>

        <div class="sub-section">
          <h4 class="sub-title">属性道具</h4>
          <template v-if="resultData.attribute_purchases.length > 0">
            <el-card
              v-for="group in resultData.attribute_purchases"
              :key="group.attribute_name"
              class="purchase-card"
              shadow="never"
            >
              <div slot="header" class="costume-card-header">
                <span>{{ group.attribute_name }}</span>
                <span class="purchase-summary">
                  总次数: {{ group.total_quantity }} | 总值: +{{ group.total_value }}
                </span>
              </div>
              <el-table :data="group.purchases" style="width: 100%" border>
                <el-table-column prop="quantity" label="数量" width="80" />
                <el-table-column prop="value" label="属性值" width="100">
                  <template slot-scope="scope">+{{ scope.row.value }}</template>
                </el-table-column>
                <el-table-column label="购买时间" width="180">
                  <template slot-scope="scope">{{ formatTime(scope.row.purchase_time) }}</template>
                </el-table-column>
                <el-table-column label="购买时所在节点">
                  <template slot-scope="scope">
                    <template v-if="scope.row.node_info">
                      <div>{{ scope.row.node_info.node_title }}</div>
                      <div v-if="scope.row.node_info.entry_conditions" class="entry-conditions">
                        <el-tag
                          v-for="(cond, idx) in formatConditions(scope.row.node_info.entry_conditions)"
                          :key="idx"
                          size="mini"
                          type="info"
                          style="margin: 2px;"
                        >{{ cond }}</el-tag>
                      </div>
                    </template>
                    <span v-else style="color: #999;">-</span>
                  </template>
                </el-table-column>
              </el-table>
            </el-card>
          </template>
          <div v-else class="empty-text">暂无属性道具购买数据</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { getLogicCheck } from '@/api/logicCheck'
import { Game, getGames } from '@/api/game'

@Component
export default class LogicCheck extends Vue {
  @Prop() readonly game!: Game

  allGames: Game[] = []
  currentGameId: number | null = null

  loading = false
  userId = ''
  resultData: any = null

  get selectedGame(): Game | null {
    if (this.currentGameId) {
      const found = this.allGames.find(g => g.id === this.currentGameId)
      if (found) return found
    }
    return this.game || null
  }

  @Watch('game', { immediate: true })
  onGamePropChange(newGame: Game) {
    if (newGame && newGame.id) {
      this.currentGameId = newGame.id
    }
  }

  async mounted() {
    await this.loadAllGames()
  }

  async loadAllGames() {
    try {
      this.allGames = await getGames()
    } catch {
      // ignore
    }
  }

  onGameSelect(gameId: number) {
    this.currentGameId = gameId
    this.resultData = null
  }

  formatTime(isoStr: string | null): string {
    if (!isoStr) return '-'
    const d = new Date(isoStr)
    if (isNaN(d.getTime())) return isoStr
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  }

  formatConditions(conditions: any): string[] {
    if (!conditions) return []
    if (Array.isArray(conditions)) {
      return conditions.map((c: any) => {
        if (typeof c === 'string') return c
        if (c.type && c.value !== undefined) return `${c.type}: ${c.value}`
        return JSON.stringify(c)
      })
    }
    if (typeof conditions === 'object') {
      return Object.entries(conditions).map(([k, v]) => `${k}: ${v}`)
    }
    return [String(conditions)]
  }

  async handleSearch() {
    const uid = parseInt(this.userId)
    if (!uid) {
      this.$message.warning('请输入有效的用户ID')
      return
    }
    if (!this.currentGameId) {
      this.$message.warning('请先选择游戏')
      return
    }
    this.loading = true
    try {
      const res = await getLogicCheck(uid, this.currentGameId)
      if (res && res.data) {
        this.resultData = res.data
      }
    } catch (error) {
      console.error('获取逻辑校验数据失败:', error)
      this.$message.error('获取数据失败')
    } finally {
      this.loading = false
    }
  }
}
</script>

<style scoped>
.logic-check-wrapper {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.game-selector {
  margin-bottom: 16px;
}

.filter-section {
  margin-bottom: 20px;
}

.filter-content {
  display: flex;
  align-items: center;
  gap: 15px;
}

.filter-item {
  display: flex;
  align-items: center;
}

.filter-label {
  margin-right: 8px;
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}

.result-section {
  margin-bottom: 20px;
  background: white;
  padding: 15px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.section-title {
  margin: 0 0 15px 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  border-left: 4px solid #409eff;
  padding-left: 10px;
}

.sub-section {
  margin-bottom: 20px;
}

.sub-title {
  margin: 0 0 10px 0;
  font-size: 14px;
  font-weight: 600;
  color: #606266;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #409eff;
  padding: 10px 0;
}

.costume-card {
  margin-bottom: 12px;
}

.costume-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
}

.purchase-card {
  margin-bottom: 12px;
}

.purchase-summary {
  font-size: 13px;
  color: #909399;
  font-weight: normal;
}

.entry-conditions {
  margin-top: 4px;
}

.empty-text {
  color: #909399;
  text-align: center;
  padding: 20px;
  font-size: 14px;
}

::v-deep .el-table th {
  background-color: #f5f7fa;
}
</style>
